import React, { useEffect, useState, useRef, useCallback } from "react";
import { cqAPI, getBid, loadCustomers, Auth } from "../../lib/Auth";
import {
  Box,
  Flex,
  Text,
  VStack,
  HStack,
  Button,
  useTheme,
  useColorMode,
  IconButton,
  Image,
  useToast,
  Select,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  useDisclosure,
  FormControl,
  FormLabel,
  Input,
  Link,
  useBreakpointValue,
  useColorModeValue,
  Spacer,
  Popover,
  PopoverTrigger,
  PopoverContent,
  PopoverArrow,
  PopoverBody,
} from "@chakra-ui/react";
import EditableText from "../EditableText/EditableText";
import { useNavigate } from "react-router-dom";
import "./AllCustomers.css";
import {
  AddIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  ChevronRightIcon,
} from "@chakra-ui/icons";
import { Autocomplete, useJsApiLoader } from "@react-google-maps/api";
import debounce from "lodash.debounce";
import { components } from "react-select";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSearch } from "@fortawesome/free-solid-svg-icons";
import AsyncSelect from "react-select/async";

const libraries = ["places"];
const isPartner =
  window.location.hostname.includes("adec") ||
  window.location.hostname.includes("a-dec");

function AllCustomers() {
  const googleMap = useJsApiLoader({
    id: "google-maps-script",
    googleMapsApiKey: process.env.REACT_APP_MAP_API,
    libraries,
  });
  const [customers, setCustomers] = useState([]);
  const [expandedRows, setExpandedRows] = useState({});
  const [bundles, setBundles] = useState([]);
  const navigate = useNavigate();
  const autocompleteRef = useRef({});
  const {
    isOpen: isNewBusinessOpen,
    onOpen: onNewBusinessOpen,
    onClose,
  } = useDisclosure();
  const onNewBusinessClose = () => onClose();

  const theme = useTheme();
  const { colorMode } = useColorMode();
  const hoverBgColor =
    colorMode === "light" ? theme.colors.gray[50] : theme.colors.gray[700];

  // Fetch all customers
  const fetchAllCustomers = async () => {
    const bid = getBid();
    const response = bid
      ? await cqAPI.get(`/locations?parents_only=1`)
      : await cqAPI.get(`/locations?parents_only=1&bid=`);
    if (response?.type === "ok") {
      setCustomers(response.data);
    }
  };

  // Fetch bundles if partner
  const fetchBundles = async () => {
    const response = await cqAPI.get(
      "/settings/subscriptions/bundles"
    );
    if (response?.type === "ok") setBundles(response.data.bundles);
  };

  useEffect(() => {
    fetchAllCustomers();
    if (isPartner) fetchBundles();
  }, []);

  const toggleExpand = (id) => {
    setExpandedRows((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const updateBusinessProfile = async (
    customer_id,
    field,
    value
  ) => {
    const response = await cqAPI.put(
      `/settings/business?bid=${customer_id}`,
      {
        field: "groupname",
        value,
      }
    );
    if (response.type === "ok") {
      setCustomers(
        customers.map((c) =>
          c.bid === customer_id
            ? { ...c, groupname: value }
            : c
        )
      );
      return true;
    }
    return false;
  };

  // Helper to format dates
  const fmt = (dateStr) =>
    dateStr
      ? new Date(dateStr).toLocaleDateString("en-US")
      : "";

  /**
   * The NewBusinessModal definition.
   */
  function NewBusinessModal({
    isOpen,
    isMapsLoaded,
    onClose,
    autocompleteRef,
    customers,
    bundles,
  }) {
    const [selectedPlace, setSelectedPlace] = useState(null);
    const [selectedGroup, setSelectedGroup] = useState("");
    const [selectedPlan, setSelectedPlan] = useState("None");
    const [activationDate, setActivationDate] = useState("");

    const toast = useToast();
    const options = { types: ["establishment"] };

    const handleGroupSelect = (e) =>
      setSelectedGroup(e.target.value);

    const handlePlaceSelect = () => {
      const place =
        autocompleteRef.current.getPlace();
      const isDentalOffice =
        place.types &&
        (place.types.includes("dentist") ||
          place.types.includes("doctor"));
      if (!isDentalOffice) {
        toast({
          title: "Invalid selection.",
          description:
            "You can only select a dental office.",
          status: "error",
          duration: 9000,
          isClosable: true,
        });
        return;
      }
      setSelectedPlace({
        name: place.name,
        address: place.formatted_address,
        location: place.geometry.location,
        place,
      });
    };

    const handleBack = () => setSelectedPlace(null);

    const confirmSelection = async () => {
      if (
        isPartner &&
        (selectedPlan === "None" ||
          !activationDate)
      ) {
        toast({
          title: "Missing required fields",
          description:
            "Plan and Activation Date cannot be empty.",
          status: "error",
          duration: 9000,
          isClosable: true,
        });
        return;
      }
      onClose();
      const requestBody = {
        place: selectedPlace.place,
        ...(selectedGroup
          ? { parent: selectedGroup }
          : {}),
        ...(isPartner
          ? {
              start_date: activationDate,
              bundle_id: selectedPlan,
            }
          : {}),
      };
      const response = await cqAPI.post(
        "/settings/business",
        requestBody
      );
      if (response.type === "ok")
        fetchAllCustomers();
      else if (response.msg) {
        toast({
          title: "Error",
          description: response.msg,
          status: "error",
          duration: 9000,
          isClosable: true,
        });
      }
    };

    return (
      <Modal
        isOpen={isOpen}
        onClose={onClose}
      >
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            Select a Dental Office
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            {isMapsLoaded && selectedPlace ? (
              <>
                <Text fontWeight="bold">
                  {selectedPlace.name}
                </Text>
                <Text mb={4}>
                  {selectedPlace.address}
                </Text>
                <Box boxSize="sm">
                  <Image
                    src={`https://maps.googleapis.com/maps/api/staticmap?center=${selectedPlace.location.lat()},${selectedPlace.location.lng()}&zoom=15&size=400x300&markers=|${selectedPlace.location.lat()},${selectedPlace.location.lng()}&key=${process.env.REACT_APP_MAP_API}`}
                    alt="Selected Place"
                  />
                </Box>
                <Button
                  colorScheme="blue"
                  mt={4}
                  onClick={confirmSelection}
                >
                  Confirm Selection
                </Button>
                <Button
                  colorScheme="gray"
                  mt={4}
                  ml={3}
                  onClick={handleBack}
                >
                  Back
                </Button>
              </>
            ) : (
              <>
                <FormControl
                  id="group-select"
                  mt={4}
                >
                  <FormLabel>
                    Select a Group
                  </FormLabel>
                  <Select
                    onChange={handleGroupSelect}
                    placeholder="New Group"
                  >
                    <option value="">
                      New Group
                    </option>
                    {customers.map((c) => (
                      <option
                        key={c.bid}
                        value={c.bid}
                      >
                        {c.groupname || c.name}
                      </option>
                    ))}
                  </Select>
                </FormControl>
                {isPartner && (
                  <>
                    <FormControl
                      id="plan-select"
                      mt={4}
                      isRequired
                    >
                      <FormLabel>
                        Plan
                      </FormLabel>
                      <Select
                        value={
                          selectedPlan
                        }
                        onChange={(e) =>
                          setSelectedPlan(
                            e.target.value
                          )
                        }
                      >
                        <option value="None">
                          None
                        </option>
                        {bundles.map((b) => (
                          <option
                            key={b._id}
                            value={b._id}
                          >
                            {b.name}
                          </option>
                        ))}
                      </Select>
                    </FormControl>
                    <FormControl
                      id="activation-date"
                      mt={4}
                      isRequired
                    >
                      <FormLabel>
                        Activation Date
                      </FormLabel>
                      <Input
                        type="date"
                        value={
                          activationDate
                        }
                        onChange={(e) =>
                          setActivationDate(
                            e.target.value
                          )
                        }
                      />
                    </FormControl>
                  </>
                )}
                <FormControl
                  id="google-maps-autocomplete"
                  pt={4}
                >
                  <FormLabel>
                    Dental Office
                  </FormLabel>
                  {googleMap.isLoaded && (
                    <Autocomplete
                      options={options}
                      onPlaceChanged={
                        handlePlaceSelect
                      }
                      onLoad={(auto) =>
                        (autocompleteRef.current =
                          auto)
                      }
                    >
                      <Input
                        type="text"
                        placeholder="Type to search..."
                      />
                    </Autocomplete>
                  )}
                </FormControl>
              </>
            )}
          </ModalBody>
          <ModalFooter>
            {!selectedPlace && (
              <Button
                colorScheme="blue"
                mr={3}
                onClick={onClose}
              >
                Close
              </Button>
            )}
          </ModalFooter>
        </ModalContent>
      </Modal>
    );
  }

  const bgColor = useColorModeValue(
    theme.colors.lightBackground,
    theme.colors.darkBackground
  );
  const textColor = useColorModeValue(
    theme.colors.lightText,
    theme.colors.darkText
  );
  const secondaryTextColor = useColorModeValue(
    theme.colors.gray[600],
    theme.colors.gray[400]
  );
  const searchBgColor = useColorModeValue(
    "#f0f4f8",
    "#2d3748"
  );
  const mobileMarginLeft = useBreakpointValue({
    base: "20px",
    md: "0",
  });
  const searchHoverBgColor = useColorModeValue(
    theme.colors.gray[200],
    theme.colors.gray[700]
  );
  const searchResultBgColor = useColorModeValue(
    theme.colors.lightBackground,
    "#0f1a25"
  );

  const formatLocationOption = (option) => (
    <Flex direction="column" py={1}>
      {/* first line: dot + name */}
      <Flex align="center">
        <Box
          as="span"
          w={2}
          h={2}
          borderRadius="full"
          bg={option.start_date ? "green.500" : "gray.500"}
          mr={2}
        />
        <Text>{option.name}</Text>
      </Flex>
      {/* second line: dates */}
      <Text fontSize="sm" color="gray.500" pl={4}>
        Created: {fmt(option.created)}
        {option.start_date
          ? `  ·  Activated: ${fmt(option.start_date)}`
          : ""}
      </Text>
    </Flex>
  );
  

  const DropdownIndicator = (props) =>
    components.DropdownIndicator ? (
      <components.DropdownIndicator {...props}>
        <FontAwesomeIcon icon={faSearch} />
      </components.DropdownIndicator>
    ) : null;
  const _loadSuggestions = (query, cb) =>
    loadCustomers(query).then(cb);
  const debouncedLoadCustomers = debounce(
    _loadSuggestions,
    1000
  );
  const handleSelectChange = (opt) => {
    const bid = opt?._id?.toString();
    if (bid) navigate(`/?bid=${bid}`);
  };
  
  const loadOptions = useCallback(
      debounce((inputValue, callback) => {
        loadCustomers(inputValue).then((options) => callback(options));
      }, 500),
      []
    );

    /* cancel debounce on unmount to prevent state updates after unmount */
    useEffect(() => {
      return () => loadOptions.cancel();
    }, [loadOptions]);

  return (
    <Box
      p={4}
      overflowY="auto"
      maxHeight="100vh"
      width="100%"
    >
      <NewBusinessModal
        isOpen={isNewBusinessOpen}
        isMapsLoaded={googleMap.isLoaded}
        onClose={onNewBusinessClose}
        autocompleteRef={autocompleteRef}
        customers={customers}
        bundles={bundles}
      />
      <Flex
        gap={8}
        mt={2}
        ml={2}
        mr={4}
        mb={4}
      >
        <Box
          width="100%"
          id="location-search-bar"
        >
      <AsyncSelect
        cacheOptions
        defaultOptions
        loadOptions={loadOptions}
        formatOptionLabel={formatLocationOption}
        getOptionValue={(opt) => opt._id}
        placeholder="Search Locations"
        components={{ DropdownIndicator }}
        className="search-input"
        onChange={handleSelectChange}
      />
        </Box>
        <Spacer />
        {Auth.user?.isSuperAdmin && (
          <Button
            minWidth="150px"
            leftIcon={<AddIcon />}
            onClick={
              onNewBusinessOpen
            }
          >
            Add Location
          </Button>
        )}
      </Flex>
      <Flex
        p={2}
        bg={useColorModeValue(
          "gray.100",
          "gray.900"
        )}
        fontWeight="semibold"
      >
        <HStack spacing={8} flex="1">
          <Box minWidth="40px" />
          <Flex flexBasis="40%">
            Business
          </Flex>
          <Flex flexBasis="20%">
            Subscription Start
          </Flex>
          <Flex flexBasis="20%">
            Account Creating Date
          </Flex>
          <Box flexBasis="10%" />
          <Box flexBasis="10%" />
        </HStack>
      </Flex>
      <VStack
        spacing={4}
        align="stretch"
      >
        {customers.map((c) => (
          <Box key={c.bid} className="customer-entry">
            <Flex
              p={2}
              _hover={{
                bg: hoverBgColor,
                cursor: "pointer",
              }}
            >
              <HStack
                spacing={8}
                flex="1"
                align="center"
              >
                <Box minWidth="40px">
                  {c.locations?.length > 1 && (
                    <IconButton
                      icon={
                        expandedRows[c.bid] ? (
                          <ChevronUpIcon />
                        ) : (
                          <ChevronDownIcon />
                        )
                      }
                      aria-label="Expand"
                      id={`expand-${c.bid}`}
                      onClick={() =>
                        toggleExpand(c.bid)
                      }
                    />
                  )}
                </Box>
                <Flex flexBasis="40%">
                  <EditableText
                    initialValue={
                      c.groupname || c.name
                    }
                    placeholder="Group Name"
                    pattern={/[a-zA-Z]+/}
                    invalid="Cannot be empty"
                    onSave={(v) =>
                      updateBusinessProfile(
                        c.bid,
                        "groupname",
                        v
                      )
                    }
                    textClicked={()=>navigate(`/?bid=${c.bid}`)}
                    editable={
                      Auth.user?.isSuperAdmin
                    }
                  />
                </Flex>
                <Flex
                  flexBasis="20%"
                  align="center"
                >
                  <Popover trigger="hover">
                    <PopoverTrigger>
                      <Box
                        as="span"
                        w={3}
                        h={3}
                        borderRadius="full"
                        bg={
                          c.start_date
                            ? "green.500"
                            : "gray.500"
                        }
                        mr={2}
                        cursor="pointer"
                      />
                    </PopoverTrigger>
                    <PopoverContent>
                      <PopoverArrow />
                      <PopoverBody fontSize="sm">
                        Green = active account
                        <br />
                        Gray = never activated
                      </PopoverBody>
                    </PopoverContent>
                  </Popover>
                  <Text>
                    {fmt(c.start_date)}
                  </Text>
                </Flex>
                <Text
                  flexBasis="20%"
                  onClick={() =>
                    navigate(`/?bid=${c.bid}`)
                  }
                >
                  {fmt(c.created)}
                </Text>
                <Text
                  flexBasis="10%"
                  onClick={() =>
                    navigate(`/?bid=${c.bid}`)
                  }
                >
                  {c.locations?.length}
                </Text>
                <Flex
                  flexBasis="10%"
                  justify="flex-end"
                  className="navigate-to-business"
                  onClick={() =>
                    navigate(`/?bid=${c.bid}`)
                  }
                >
                  <ChevronRightIcon />
                </Flex>
              </HStack>
            </Flex>
            {expandedRows[c.bid] && (
              <VStack
                align="stretch"
                pl={12}
                mt={2}
              >
                {c.locations.map((loc) => (
                  <Link
                    key={loc._id}
                    fontSize="sm"
                    id={`location-link-${loc._id}`}
                    onClick={() =>
                      navigate(`/?bid=${loc._id}`)
                    }
                  >
                    {loc.name}
                  </Link>
                ))}
              </VStack>
            )}
          </Box>
        ))}
      </VStack>
    </Box>
  );
}

export default AllCustomers;